import React from 'react';
import classNames from 'classnames';
import Immutable from 'immutable';

import { Block, Paragraph } from '@uncinc/react-drupal-core';
import { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { Container } from '@uncinc/uncinc-react-kitchen-sink';

import { DEFAULT_LANGUAGE } from '../../../../config/constants';
import { NodeProject } from '../../../../types/uncinc_profile/nodes';
import {
  ProjectHero,
  ProjectMap,
  RelatedContentDefault as RelatedContent,
  WysiwygField,
} from '../../../../components';

interface NodeProjectProps extends React.ComponentPropsWithoutRef<'div'> {
  entity: ImmutableEntity<NodeProject>;
  className?: string;
}

// Project content type. Used for 'Scholen'.
const ProjectPage: React.FC<NodeProjectProps> = ({ entity, className = '', ...props }) => {
  const node = entity.toJS();
  const hasTextualContent = node.body.length > 0 || node.field_paragraphs.length > 0;

  const classes = classNames('NodeProject', className, {
    'NodeProject--has-textual-content': hasTextualContent,
  });

  const heroColumnClasses = hasTextualContent ? 'col-lg-5 col-xl-6' : 'col-12';

  const contactFormBlockEntity = entity.getIn(['field_contact_form_block', 0, 'entity'], Immutable.Map());

  return (
    <article itemType="http://schema.org/WebPage" className={classes} {...props}>
      <Container>
        <div className="row">
          <div className={heroColumnClasses}>
            <div className="mt-6">
              <ProjectHero
                logo={node.field_media_item[0]}
                title={node.title[0].value}
                status={node.field_status[0].entity.name[0].value}
                types={node.field_type.map(({ entity: term }) => (term.name[0].value))}
                location={node.field_location[0].entity}
                url={node.field_link?.[0]?.alias || node.field_link?.[0]?.uri}
              />
            </div>
          </div>

          {hasTextualContent && (
            <div className="col-lg-7 col-xl-6">
              {node.body.length > 0 && (
                <div className="content-section mt-lg-6 mb-0">
                  <WysiwygField
                    className="body--large"
                    property="schema:text"
                    html={node.body?.[0]?.processed}
                  />
                </div>
              )}

              {node.field_paragraphs.length > 0 && (
                <div>
                  {node.field_paragraphs.map(({ target_id: id, entity: paragraph }) => {
                    const language = paragraph.langcode[0].value || DEFAULT_LANGUAGE;

                    return (
                      <Paragraph
                        key={`paragraph:${id}:${language}`}
                        id={id}
                        entity={Immutable.Map(paragraph)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="col-12">
            <RelatedContent args={[node.nid[0].value]} type="by_projects" />
          </div>

          <div className="col-12">
            <div className="content-section">
              <ProjectMap defaultMarker={node.nid[0].value} />
            </div>
          </div>

        </div>

        {contactFormBlockEntity && (
          <Block entity={contactFormBlockEntity} />
        )}
      </Container>
    </article>
  );
};

export default ProjectPage;
